import React from 'react';
import './NFTCard.css';

const NFTCard = ({ nft, rarityMethod, collection }) => {
  // Extract the number from the NFT name for image URL generation
  const getTokenId = (name) => {
    const match = name.match(/#(\d+)/);
    return match ? match[1] : '1';
  };

  // Generate image URL based on collection and token ID
  const getImageUrl = (nft, collection) => {
    const tokenId = getTokenId(nft.name);

    switch (collection) {
      case 'pixel':
        return `/images/pixel/${tokenId}.jpg`;
      case 'ladies':
        return `/images/ladies/${tokenId}.png`;
      case 'mutant':
        return `/images/mutants/${tokenId}.jpg`; // JPG for mutants
      case 'pja':
        return `/images/pja/${tokenId}.png`; // PNG for pja
      case 'topg':
        return `/images/tga/${tokenId}.jpg`;
      case 'mula_trees':
        return `/images/trees/${tokenId}.png`; // PNG for trees
      case 'xwar':
        // Handle special case for X-WAR Custom Apes
        if (nft.name.includes('Custom')) {
          const customImagePath = `/images/xwar/custom${tokenId}.png`;
          // console.log(`🎯 X-WAR Custom Ape detected!`);
          // console.log(`   Full NFT Name: "${nft.name}"`);
          // console.log(`   Extracted Token ID: "${tokenId}"`);
          // console.log(`   Final Generated Path: "${customImagePath}"`);
          // console.log(`   Expected file location: ./public${customImagePath}`);
          return customImagePath;
        }
        return `/images/xwar/${tokenId}.png`; // PNG for regular xwar
      case 'og':
      default:
        // Handle OG custom NFTs
        if (nft.isCustom || nft.name.includes('Custom')) {
          const customMatch = nft.name.match(/Custom #(\d+)/);
          const customNumber = customMatch ? customMatch[1] : tokenId;
          const customImagePath = `/images/og/custom%23${customNumber}.png`;
          // console.log(`   Expected file location: ./public${customImagePath}`);
          return customImagePath;
        }
        // For regular OG collection, use png
        return `/images/og/${tokenId}.png`;
    }
  };

  // Generate external URLs for NFT
  const getExternalUrls = (nft, collection) => {
    const urls = [];

    // Collections that are available on Sologenic
    const sologenicCollections = ['og', 'pja', 'xwar'];
    const isOnSologenic = sologenicCollections.includes(collection);

    if (nft.NFTokenID && nft.NFTokenID.trim() !== '') {
      // Bithomp URL for all collections with NFTokenID (first link)
      urls.push({
        type: 'bithomp',
        url: `https://bithomp.com/en/nft/${nft.NFTokenID}`,
        icon: '/bithompIcon.png',
        alt: 'View on Bithomp',
      });

      // Add Sologenic URL only for supported collections
      if (isOnSologenic) {
        urls.push({
          type: 'sologenic',
          url: `https://sologenic.org/nft/${nft.NFTokenID}`,
          icon: '/soloIcon.webp',
          alt: 'View on Sologenic',
        });
      }

      // XRP Cafe URL for all collections with NFTokenID
      urls.push({
        type: 'xrpcafe',
        url: `https://xrp.cafe/nft/${nft.NFTokenID}`,
        icon: '/xrpcafeIcon.webp',
        alt: 'View on XRP Cafe',
      });
    } else if (nft.currency && nft.xls14_issuer && isOnSologenic) {
      // Only Sologenic XLS14 URL when no NFTokenID but has currency (and collection is on Sologenic)
      urls.push({
        type: 'sologenic-xls14',
        url: `https://sologenic.org/nfts/${nft.xls14_issuer}/${nft.currency}`,
        icon: '/soloIcon.webp',
        alt: 'View on Sologenic',
      });
    }

    return urls;
  };

  // Get rarity value based on method
  const getRarityValue = (nft, method) => {
    // Handle consolidated ranking structure
    if (nft.rankings) {
      let rankingKey;
      switch (method) {
        case 'sniper':
          rankingKey = 'rarity_sniper';
          break;
        case 'average':
          rankingKey = 'average_trait_rarity';
          break;
        case 'score':
          rankingKey = 'rarity_score';
          break;
        case 'statistical':
          rankingKey = 'statistical_rarity';
          break;
        case 'jaccard':
          rankingKey = 'jaccard_distance';
          break;
        case 'traitRating':
          rankingKey = 'trait_rarity_rating';
          break;
        default:
          rankingKey = 'rarity_score';
      }

      const ranking = nft.rankings[rankingKey];
      if (ranking && ranking.score !== undefined) {
        // Show 'Custom' for custom NFTs instead of score 0
        if (nft.isCustom && ranking.score === 0) {
          return 'Custom';
        }
        return ranking.score.toFixed(2);
      }
    }

    // Fallback to old structure for backwards compatibility
    if (method === 'average') {
      return nft.averageTraitRarity?.toFixed(2) || 'N/A';
    } else {
      return nft.rarityScore?.toFixed(2) || nft.averageTraitRarity?.toFixed(2) || 'N/A';
    }
  };

  // Handle image load error with noImage fallback
  const handleImageError = (e) => {
    // const failedUrl = e.target.src;
    // console.log(`❌ Image failed to load: ${failedUrl}`);
    // console.log(`❌ NFT Name: ${nft.name}`);
    // console.log(`❌ Collection: ${collection}`);
    // console.log(`� Using noImage.png fallback`);
    e.target.src = '/noImage.png';
  };

  // Get rank value based on method
  const getRankValue = (nft, method) => {
    // Ladies are ranked purely by their edition number
    if (collection === 'ladies') {
      return getTokenId(nft.name);
    }

    // Handle consolidated ranking structure
    if (nft.rankings) {
      let rankingKey;
      switch (method) {
        case 'sniper':
          rankingKey = 'rarity_sniper';
          break;
        case 'average':
          rankingKey = 'average_trait_rarity';
          break;
        case 'score':
          rankingKey = 'rarity_score';
          break;
        case 'statistical':
          rankingKey = 'statistical_rarity';
          break;
        case 'jaccard':
          rankingKey = 'jaccard_distance';
          break;
        case 'traitRating':
          rankingKey = 'trait_rarity_rating';
          break;
        default:
          rankingKey = 'rarity_score';
      }

      const ranking = nft.rankings[rankingKey];
      if (ranking && ranking.rank !== undefined) {
        // Show 'Custom' for custom NFTs instead of rank 0
        if (nft.isCustom && ranking.rank === 0) {
          return 'Custom';
        }
        return ranking.rank;
      }
    }

    // Fallback to old structure
    return nft.rank || 'N/A';
  };

  return (
    <div className='nft-card'>
      <div className='card-image-container'>
        <img
          src={getImageUrl(nft, collection)}
          alt={nft.name}
          className='card-image'
          onError={handleImageError}
        />
        <div className='rank-badge'>#{getRankValue(nft, rarityMethod)}</div>
      </div>

      <div className='card-content'>
        <h3 className='nft-name'>{nft.name}</h3>

        <div className='rarity-info'>
          <div className='rarity-score'>
            <span className='rarity-label'>
              {rarityMethod === 'sniper'
                ? 'Rarity Sniper Score'
                : rarityMethod === 'average'
                ? 'Avg Trait Rarity'
                : rarityMethod === 'statistical'
                ? 'Statistical Rarity'
                : rarityMethod === 'jaccard'
                ? 'Jaccard Distance'
                : rarityMethod === 'traitRating'
                ? 'Trait Rarity Rating'
                : 'Rarity Score'}
              :
            </span>
            <span className='rarity-value'>{getRarityValue(nft, rarityMethod)}</span>
          </div>
        </div>

        <div className='traits-section'>
          <div className='traits-header'>
            <h4 className='traits-title'>Traits</h4>
            <div className='external-links'>
              {getExternalUrls(nft, collection).map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={`external-link ${link.type}`}
                  title={link.alt}
                >
                  <img src={link.icon} alt={link.alt} className='external-icon' />
                </a>
              ))}
            </div>
          </div>
          <div className='traits-list'>
            {nft.attributes?.slice(0, 4).map((trait, index) => (
              <div key={index} className='trait-item'>
                <span className='trait-type'>{trait.trait_type}:</span>
                <span className='trait-value'>{trait.value}</span>
              </div>
            ))}
            {nft.attributes?.length > 4 && (
              <div className='more-traits'>+{nft.attributes.length - 4} more traits</div>
            )}
          </div>
        </div>

        {(nft.edition || nft.drop) && (
          <div className='edition-info'>
            {collection === 'og' && nft.drop ? (
              <>
                <span className='edition-label'>Drop:</span>
                <span className='edition-value'>#{nft.drop}</span>
              </>
            ) : nft.edition ? (
              <>
                <span className='edition-label'>Edition:</span>
                <span className='edition-value'>#{nft.edition}</span>
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default NFTCard;
